import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  FileText,
  File,
  Globe,
  Presentation,
  PlayCircle,
  MessageCircle,
  Shield,
} from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { FavoriteButton } from '../../../components/FavoriteButton';
import { useAuth } from '@/auth/hooks/useAuth';
import { queryKeys } from '@/lib/query-keys';
import { api } from '@/lib/api';

interface ResourceCardProps {
  icon: React.ReactNode;
  title: string;
  description: string;
  buttonLabel: string;
  href: string;
  external?: boolean;
}

function ResourceCard({
  icon,
  title,
  description,
  buttonLabel,
  href,
  external,
}: ResourceCardProps) {
  return (
    <Card className="h-full">
      <CardContent className="flex flex-col items-center justify-between gap-6 p-8 h-full text-center">
        <div className="flex flex-col items-center gap-6">
          <div className="text-primary">{icon}</div>
          <div className="space-y-2">
            <h3 className="text-lg font-semibold">{title}</h3>
            <p className="text-sm text-muted-foreground">{description}</p>
          </div>
        </div>
        {external ? (
          <a href={href} target="_blank" rel="noopener noreferrer" className="w-full">
            <Button variant="default" size="lg" className="w-full">
              {buttonLabel}
            </Button>
          </a>
        ) : (
          <Link to={href} className="w-full">
            <Button variant="secondary" size="lg" className="w-full">
              {buttonLabel}
            </Button>
          </Link>
        )}
      </CardContent>
    </Card>
  );
}

const RESOURCES: ResourceCardProps[] = [
  {
    icon: <FileText className="h-10 w-10" />,
    title: 'Simple Summary',
    description: 'A one-page non-technical overview of Fias.',
    buttonLabel: 'Read Summary',
    href: '/docs/Fias_Simple_Summary.pdf',
    external: true,
  },
  {
    icon: <File className="h-10 w-10" />,
    title: 'Executive Summary',
    description: 'A concise executive overview of Fias.',
    buttonLabel: 'Read Summary',
    href: '/docs/Fias_Executive_Summary.pdf',
    external: true,
  },
  {
    icon: <Globe className="h-10 w-10" />,
    title: 'Website',
    description: 'Visit our main website for the latest updates and news.',
    buttonLabel: 'Visit fias.io',
    href: 'https://fias.io',
    external: true,
  },
  {
    icon: <Presentation className="h-10 w-10" />,
    title: 'Presentation Deck',
    description: 'View our presentation deck with key concepts and vision.',
    buttonLabel: 'View Deck',
    href: '/docs/Fias_Overview.pdf',
    external: true,
  },
  {
    icon: <PlayCircle className="h-10 w-10" />,
    title: 'Intro Videos',
    description: 'Watch introduction and information videos about Fias.',
    buttonLabel: 'Watch',
    href: 'https://www.youtube.com/playlist?list=PLQdfOryRxAcel4oLrZX6Y_89JV4q030mZ',
    external: true,
  },
  {
    icon: <FileText className="h-10 w-10" />,
    title: 'Whitepaper',
    description: 'Deep dive into our technical architecture and methodology.',
    buttonLabel: 'Read',
    href: '/docs/Fias-WhitepaperV2-1.pdf',
    external: true,
  },
];

export function LearnAboutFiasResourcesPage() {
  const { user } = useAuth();
  const isAdmin = useMemo(() => user?.roles?.includes('admin') ?? false, [user?.roles]);

  const { data: dynamicResources } = useQuery({
    queryKey: queryKeys.learnAboutFias.resources(),
    queryFn: () => api.getLearnAboutFiasResources(),
  });

  return (
    <div className="max-w-4xl mx-auto px-6 py-8">
      <div className="flex flex-col items-center gap-4 mb-8">
        <div className="flex items-center gap-2">
          <h1 className="text-3xl font-bold text-center">Learn About Fias</h1>
          <FavoriteButton archeId="arche_learn_about_fias" />
        </div>
        <p className="text-muted-foreground text-center">
          Explore resources to learn more about Fias and the future of monetizing AI.
        </p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
        {RESOURCES.map((resource) => (
          <ResourceCard key={resource.title} {...resource} />
        ))}
        {dynamicResources?.resources.map((resource) => (
          <ResourceCard
            key={resource.docId}
            icon={<FileText className="h-10 w-10" />}
            title={resource.title}
            description={resource.description ?? ''}
            buttonLabel={resource.sourceUrl ? 'View' : 'Learn More'}
            href={resource.sourceUrl ?? '#'}
            external={!!resource.sourceUrl}
          />
        ))}
      </div>

      <div className="flex justify-center mt-8">
        <Card className="w-full sm:w-1/2">
          <CardContent className="flex flex-col items-center gap-6 p-8 text-center">
            <MessageCircle className="h-10 w-10 text-primary" />
            <div className="space-y-2">
              <h3 className="text-lg font-semibold">Ask AI</h3>
              <p className="text-sm text-muted-foreground">
                Have questions? Ask our AI assistant about Fias.
              </p>
            </div>
            <Link to="/a/arche_learn_fias_chat" className="w-full">
              <Button variant="secondary" size="lg" className="w-full">
                Start Conversation
              </Button>
            </Link>
          </CardContent>
        </Card>
      </div>

      {isAdmin && (
        <div className="flex justify-center mt-4">
          <Link to="/a/arche_learn_about_fias/admin">
            <Button variant="outline" size="sm">
              <Shield className="mr-2 h-4 w-4" />
              Manage Assets
            </Button>
          </Link>
        </div>
      )}
    </div>
  );
}
